import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from '@/context/AuthContext';
import { useAudioPlayer } from '@/context/AudioPlayerContext';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Camera, MapPin, Loader2, Upload, Sparkles, Star, RefreshCw, Navigation, Info, Headphones, Bookmark, Clock, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { identifyLocation, getNearbyPlaces } from '@/lib/api';
import { PlaceCard } from './PlaceCard';

/**
 * ScanHereModal - "C'est quoi ici?" feature
 * Mode Photo: Take a photo or upload and AI identifies the location
 * Mode GPS: Get current position and find nearby places
 */
export function ScanHereModal({ isOpen, onClose, onAudioGuide }) {
  const { language } = useTranslation();
  const { startAudioGuide, isGenerating } = useAudioPlayer();
  const [mode, setMode] = useState(null); // 'photo' | 'gps'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [cameraError, setCameraError] = useState(false);
  // Version picker state
  const [versionPickerPlace, setVersionPickerPlace] = useState(null);

  // Cleanup camera on unmount or close
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMode(null);
      setResult(null);
      setError(null);
      setCapturedImage(null);
      setNearbyPlaces([]);
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
      }
    }
  }, [isOpen]);

  // Start camera for photo mode
  const startCamera = useCallback(async () => {
    setCameraError(false);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError(true);
      setError(language === 'fr' 
        ? "Caméra non disponible. Uploadez une photo."
        : 'Camera unavailable. Upload a photo.');
    }
  }, [language]);

  // Handle photo mode selection
  const handlePhotoMode = useCallback(() => {
    setMode('photo');
    setResult(null);
    setError(null);
    startCamera();
  }, [startCamera]);

  // Handle GPS mode selection
  const handleGPSMode = useCallback(async () => {
    setMode('gps');
    setResult(null);
    setError(null);
    setIsLoading(true);

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve, 
          () => {
            navigator.geolocation.getCurrentPosition(
              resolve, 
              reject, 
              { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
            );
          },
          { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
        );
      });

      const { latitude, longitude } = position.coords;
      setCurrentLocation({ lat: latitude, lng: longitude });

      const response = await getNearbyPlaces({ lat: latitude, lng: longitude, radius: 500 });
      setNearbyPlaces(response.data?.places || []);
    } catch (err) {
      console.error('GPS error:', err);
      setError(language === 'fr'
        ? 'Position indisponible. Vérifiez les permissions.'
        : 'Location unavailable. Check permissions.');
      setNearbyPlaces(getMockNearbyPlaces(language));
    } finally {
      setIsLoading(false);
    }
  }, [language]);

  // Capture photo from camera
  const capturePhoto = useCallback(() => {
    if (!videoRef.current) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.7);
    setCapturedImage(imageData);

    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }

    identifyLocationFromImage(imageData);
  }, [stream]);

  // Handle file upload
  const handleFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError(language === 'fr' ? 'Sélectionnez une image' : 'Select an image');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError(language === 'fr' ? 'Image trop grande (max 10MB)' : 'Image too large (max 10MB)');
      return;
    }

    setError(null);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const imageData = event.target.result;
      setCapturedImage(imageData);
      
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
      }
      
      identifyLocationFromImage(imageData);
    };
    reader.readAsDataURL(file);
  }, [language, stream]);

  // Handle drag & drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const fakeEvent = { target: { files: [file] } };
      handleFileUpload(fakeEvent);
    }
  }, [handleFileUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Identify location from image using AI
  const identifyLocationFromImage = useCallback(async (imageData) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await identifyLocation({ image: imageData });
      
      // Transform to PlaceCard format
      const enrichedResult = {
        ...response.data,
        name: response.data.name || 'Lieu identifié',
        category: response.data.category || 'Point d\'intérêt',
        address: response.data.address || response.data.city || '',
        description: response.data.description || '',
        rating: response.data.rating,
        reviews_count: response.data.reviews_count,
        open_now: response.data.open_now,
        maps_url: response.data.maps_url || 
          `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(response.data.name || 'location')}`
      };
      
      setResult(enrichedResult);
    } catch (err) {
      console.error('[ScanHere] Identify error:', err);
      setError(language === 'fr' 
        ? "Erreur d'identification. Réessayez."
        : 'Identification error. Try again.');
      setResult(getMockIdentifyResult(language));
    } finally {
      setIsLoading(false);
    }
  }, [language]);

  // Handle audio guide — opens version picker
  const handleAudioGuide = useCallback((place) => {
    setVersionPickerPlace({
      name: place.name,
      category: place.category || '',
      description: place.description || '',
      address: place.address || '',
      lat: place.lat,
      lng: place.lng,
      place_id: place.place_id || place.id,
    });
  }, []);

  // Called when user selects a version
  const handleVersionSelect = useCallback(async (version) => {
    if (!versionPickerPlace) return;
    const place = versionPickerPlace;
    setVersionPickerPlace(null);
    await startAudioGuide(place, { version, lang: language });
  }, [versionPickerPlace, language, startAudioGuide]);

  // Retry
  const handleRetry = useCallback(() => {
    setCapturedImage(null);
    setResult(null);
    setError(null);
    if (mode === 'photo') {
      startCamera();
    }
  }, [mode, startCamera]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        data-testid="scan-here-modal"
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

        {/* Modal */}
        <motion.div 
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="relative bg-gray-50 rounded-3xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-lg">
                <Camera className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-gray-900">
                  {language === 'fr' ? "C'est quoi ici ?" : "What's here?"}
                </h2>
                <p className="text-xs text-gray-500">
                  {language === 'fr' ? 'Photo ou GPS' : 'Photo or GPS'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-gray-100 text-gray-400 transition-colors"
              data-testid="close-scan-modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-5 overflow-y-auto max-h-[calc(90vh-80px)]">
            {/* Mode Selection */}
            {!mode && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <p className="text-sm text-gray-600 text-center mb-6">
                  {language === 'fr' 
                    ? 'Comment voulez-vous identifier ce lieu ?'
                    : 'How do you want to identify this place?'}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={handlePhotoMode}
                    className="flex flex-col items-center p-6 rounded-2xl bg-white border-2 border-gray-100 hover:border-violet-400 hover:bg-violet-50 transition-all group"
                    data-testid="photo-mode-btn"
                  >
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-100 to-fuchsia-100 group-hover:from-violet-200 group-hover:to-fuchsia-200 flex items-center justify-center mb-3 transition-colors">
                      <Camera className="w-8 h-8 text-violet-600" />
                    </div>
                    <span className="font-semibold text-gray-900">
                      {language === 'fr' ? 'Photo' : 'Photo'}
                    </span>
                    <span className="text-xs text-gray-500 mt-1">
                      {language === 'fr' ? 'Prendre ou uploader' : 'Capture or upload'}
                    </span>
                  </button>
                  <button
                    onClick={handleGPSMode}
                    className="flex flex-col items-center p-6 rounded-2xl bg-white border-2 border-gray-100 hover:border-green-400 hover:bg-green-50 transition-all group"
                    data-testid="gps-mode-btn"
                  >
                    <div className="w-16 h-16 rounded-full bg-green-100 group-hover:bg-green-200 flex items-center justify-center mb-3 transition-colors">
                      <MapPin className="w-8 h-8 text-green-600" />
                    </div>
                    <span className="font-semibold text-gray-900">
                      {language === 'fr' ? 'GPS' : 'GPS'}
                    </span>
                    <span className="text-xs text-gray-500 mt-1">
                      {language === 'fr' ? 'Autour de moi' : 'Near me'}
                    </span>
                  </button>
                </div>
              </motion.div>
            )}

            {/* Photo Mode */}
            {mode === 'photo' && (
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                {/* Camera / Captured Image / Upload Zone */}
                {!capturedImage && !result ? (
                  <div 
                    className="relative bg-gray-900 rounded-2xl overflow-hidden aspect-[4/3]"
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                  >
                    {!cameraError && stream ? (
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-6">
                        <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-4">
                          <Upload className="w-8 h-8" />
                        </div>
                        <p className="text-center text-sm mb-2">
                          {language === 'fr' ? 'Glissez une photo ici' : 'Drop a photo here'}
                        </p>
                        <p className="text-center text-xs text-white/60">
                          {language === 'fr' ? 'ou cliquez sur Uploader' : 'or click Upload'}
                        </p>
                      </div>
                    )}
                    
                    {stream && !cameraError && (
                      <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute inset-4 border-2 border-white/30 rounded-lg" />
                      </div>
                    )}
                  </div>
                ) : capturedImage && !result ? (
                  <div className="relative rounded-2xl overflow-hidden">
                    <img src={capturedImage} alt="Captured" className="w-full rounded-2xl" />
                    {isLoading && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <div className="flex flex-col items-center">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center animate-pulse">
                            <Sparkles className="w-8 h-8 text-white" />
                          </div>
                          <p className="text-white text-sm mt-3 font-medium">
                            {language === 'fr' ? 'Analyse IA en cours...' : 'AI analyzing...'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}

                {/* Error */}
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 flex items-center gap-2">
                    <X className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                {/* Actions - Camera */}
                {!capturedImage && !result && (
                  <div className="flex gap-3">
                    {stream && (
                      <Button
                        onClick={capturePhoto}
                        className="flex-1 rounded-xl bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white py-6"
                        data-testid="capture-photo-btn"
                      >
                        <Camera className="w-5 h-5 mr-2" />
                        {language === 'fr' ? 'Capturer' : 'Capture'}
                      </Button>
                    )}
                    <label className={stream ? '' : 'flex-1'}>
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleFileUpload}
                        className="hidden"
                        ref={fileInputRef}
                      />
                      <Button
                        type="button"
                        variant={stream ? "outline" : "default"}
                        className={`w-full rounded-xl py-6 ${!stream ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white' : ''}`}
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="upload-photo-btn"
                      >
                        <Upload className="w-5 h-5 mr-2" />
                        {language === 'fr' ? 'Uploader' : 'Upload'}
                      </Button>
                    </label>
                  </div>
                )}

                {/* Result - Use PlaceCard */}
                {result && !isLoading && (
                  <div className="space-y-4">
                    <PlaceCard
                      place={result}
                      onInfo={(p) => console.log('Info:', p)}
                      onAudioGuide={handleAudioGuide}
                      onBookmark={(p) => alert(language === 'fr' ? 'Sauvegardé !' : 'Saved!')}
                    />
                    
                    {/* Retry button */}
                    <Button
                      variant="outline"
                      className="w-full rounded-xl py-3"
                      onClick={handleRetry}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {language === 'fr' ? 'Réessayer' : 'Try again'}
                    </Button>
                  </div>
                )}

                {/* Back button */}
                {!result && (
                  <Button
                    variant="ghost"
                    className="w-full text-gray-600 hover:text-gray-800"
                    onClick={() => { 
                      setMode(null); 
                      setCapturedImage(null); 
                      if (stream) {
                        stream.getTracks().forEach(track => track.stop());
                        setStream(null);
                      }
                    }}
                  >
                    {language === 'fr' ? '← Changer de mode' : '← Change mode'}
                  </Button>
                )}
              </motion.div>
            )}

            {/* GPS Mode */}
            {mode === 'gps' && (
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                {/* Loading */}
                {isLoading && (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                      <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
                    </div>
                    <span className="text-gray-600 font-medium">
                      {language === 'fr' ? 'Localisation...' : 'Getting location...'}
                    </span>
                  </div>
                )}

                {/* Error */}
                {error && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
                    {error}
                  </div>
                )}

                {/* Nearby Places - Using PlaceCard for selected */}
                {!isLoading && nearbyPlaces.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2 px-1">
                      <Sparkles className="w-4 h-4 text-violet-500" />
                      {language === 'fr' ? 'Autour de vous' : 'Near you'}
                    </h3>

                    {/* Place list */}
                    <div className="space-y-2 max-h-[250px] overflow-y-auto">
                      {nearbyPlaces.slice(0, 6).map((place, i) => (
                        <motion.div
                          key={place.id || i}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.05 }}
                          className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                            result?.id === place.id 
                              ? 'bg-violet-100 border-2 border-violet-400' 
                              : 'bg-white border border-gray-100 hover:border-violet-200'
                          }`}
                          onClick={() => setResult(place)}
                        >
                          <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center text-lg">
                            {place.icon || '📍'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{place.name}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span>{place.category}</span>
                              {place.distance && <span>• {place.distance}</span>}
                              {place.rating && (
                                <span className="flex items-center gap-0.5">
                                  • <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                                  {place.rating}
                                </span>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected Place - PlaceCard */}
                {result && !isLoading && (
                  <PlaceCard
                    place={result}
                    onInfo={(p) => console.log('Info:', p)}
                    onAudioGuide={handleAudioGuide}
                    onBookmark={(p) => alert(language === 'fr' ? 'Sauvegardé !' : 'Saved!')}
                  />
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={handleGPSMode}
                    disabled={isLoading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                    {language === 'fr' ? 'Rafraîchir' : 'Refresh'}
                  </Button>
                  <Button
                    variant="ghost"
                    className="flex-1"
                    onClick={() => { setMode(null); setNearbyPlaces([]); setResult(null); }}
                  >
                    {language === 'fr' ? '← Changer' : '← Change'}
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Version Picker Overlay - positioned inside the card */}
        <AnimatePresence>
          {versionPickerPlace && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-[60] flex items-center justify-center p-6"
            >
              <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <div>
                    <h3 className="font-bold text-gray-900">Audio Guide</h3>
                    <p className="text-xs text-gray-500 truncate max-w-[220px]">{versionPickerPlace.name}</p>
                  </div>
                  <button
                    onClick={() => setVersionPickerPlace(null)}
                    className="p-2 rounded-xl hover:bg-gray-100 text-gray-400"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-5 space-y-3">
                  <p className="text-sm text-gray-600 text-center mb-2">
                    {language === 'fr' ? 'Choisissez la durée de votre visite :' : 'Choose your tour duration:'}
                  </p>
                  {[
                    { id: 'short', label: language === 'fr' ? 'Courte' : 'Short', minutes: 2, description: language === 'fr' ? "L'essentiel en 2 min" : 'Essentials in 2 min', chapters: 3, color: 'from-green-400 to-green-600', icon: '⚡' },
                    { id: 'medium', label: language === 'fr' ? 'Moyenne' : 'Medium', minutes: 5, description: language === 'fr' ? 'Visite complète en 5 min' : 'Complete tour in 5 min', chapters: 5, color: 'from-purple-500 to-violet-600', icon: '🎧' },
                    { id: 'long', label: language === 'fr' ? 'Longue' : 'Long', minutes: 10, description: language === 'fr' ? 'Immersion totale en 10 min' : 'Full immersion in 10 min', chapters: 7, color: 'from-rose-500 to-pink-600', icon: '🎭' },
                  ].map(v => (
                    <button
                      key={v.id}
                      onClick={() => handleVersionSelect(v.id)}
                      disabled={isGenerating}
                      data-testid={`scan-version-${v.id}`}
                      className="w-full flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-r from-gray-50 to-purple-50 border-2 border-gray-100 hover:border-purple-300 hover:shadow-md transition-all group active:scale-[.98] disabled:opacity-50"
                    >
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl bg-gradient-to-br ${v.color} shadow-lg flex-shrink-0`}>
                        {v.icon}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-900">{v.label}</span>
                          <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium">
                            ~{v.minutes} min
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5">{v.description}</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {v.chapters} {language === 'fr' ? 'chapitres' : 'chapters'}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-purple-500 transition-colors flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </motion.div>
    </AnimatePresence>
  );
}

// Mock data
function getMockIdentifyResult(language) {
  return {
    name: language === 'fr' ? 'Lieu identifié' : 'Identified Place',
    category: language === 'fr' ? 'Point d\'intérêt' : 'Point of interest',
    description: language === 'fr' 
      ? 'Un lieu intéressant à découvrir. Prenez le temps d\'explorer ses alentours.'
      : 'An interesting place to discover. Take time to explore its surroundings.',
    confidence: 'medium',
    maps_url: 'https://maps.google.com'
  };
}

function getMockNearbyPlaces(language) {
  return [
    { id: 'n1', name: 'Café de Flore', category: language === 'fr' ? 'Café' : 'Cafe', distance: '120m', open_now: true, icon: '☕', rating: 4.2, description: 'Café littéraire mythique de Saint-Germain-des-Prés.' },
    { id: 'n2', name: "Musée d'Orsay", category: language === 'fr' ? 'Musée' : 'Museum', distance: '450m', open_now: true, icon: '🏛️', rating: 4.7, description: 'Ancienne gare reconvertie en musée impressionniste.' },
    { id: 'n3', name: 'Le Comptoir', category: language === 'fr' ? 'Restaurant' : 'Restaurant', distance: '200m', open_now: false, icon: '🍽️', rating: 4.4, description: 'Cuisine française raffinée.' },
  ];
}

export default ScanHereModal;
